import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import { notifications } from '@myphx-engineering/phx-design-system';
import {
    type ConsentInput,
    ProfileConsentDetail,
    type ProfileConsentDetailConfig,
    consentStatuses,
} from '@phx/consents/client';

import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import {
    type ConsentDocument,
    ConsentStatus,
    type ConsentSummary,
    GetAllConsentsDocument,
    UpdateConsentsDocument,
    type UpdateConsentsMutation,
    type UpdateConsentsMutationVariables,
} from '../../../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { Loader } from '../../../common/Loader';

export const ProfileConsentDetailPage = () => {
    const { t } = useTranslation();
    const { consentId } = useParams();
    const { signOut } = useAuthContext();
    const navigate = useNavigate();

    const { data, loading, error } = useQuery(GetAllConsentsDocument);

    const [updateConsents, { loading: mutationLoading }] = useMutation<
        UpdateConsentsMutation,
        UpdateConsentsMutationVariables
    >(UpdateConsentsDocument);

    const consents = data?.app?.allConsents as ConsentSummary[];
    const consent = consents?.find((c) => c.id === consentId);
    const consentDocument = getFragment(consent?.document) as ConsentDocument;

    const handleUpdateConsents = async (input: ConsentInput[]) => {
        const updatedStatus = input[0]?.status;
        const updateInput = input.map((consent) => ({
            ...consent,
            status: consent.status as ConsentStatus,
        }));

        if (consentDocument.required && updatedStatus === 'REVOKED') {
            await updateConsents({
                variables: { input: updateInput },
            });
            signOut();
        } else {
            await updateConsents({
                variables: { input: updateInput },
                refetchQueries: [{ query: GetAllConsentsDocument }],
                awaitRefetchQueries: true,
            });

            navigate(getAbsoluteRoute('profile.consents.root'));

            notifications.show({
                message: t('consentDetails.consentUpdated', {
                    status: consentStatuses[
                        input[0]?.status as keyof typeof consentStatuses
                    ],
                }),
            });
        }
    };
    const config: ProfileConsentDetailConfig = {
        onUpdateConsents: handleUpdateConsents,
        onError: () => navigate(getAbsoluteRoute('error')),
    };

    if (loading) return <Loader />;

    if (error || !consent) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const consentData = {
        activityDate: consent.activityDate,
        document: consentDocument,
        status: consent.status,
    };

    return (
        <ProfileConsentDetail
            consent={consentData}
            config={config}
            loading={mutationLoading}
        />
    );
};
